import { SplitAuthLayout } from "@closer/ui";
import type { ReactNode } from "react";

import { ClientAreaIcon, ContractIcon, ShieldIcon } from "../components/icons";

/**
 * OTURUM EKRANLARININ KABUĞU — giriş, kayıt, şifre sıfırlama, e-posta doğrulama.
 *
 * ### Neden bir kabuk
 *
 * Yedi ayrı oturum ekranı vardı ve yedisi de aynı bloğu ("ortala, `max-w-sm` bir kart
 * koy") kendi içinde kuruyordu. Onaylı tasarımda bu ekranların hepsi aynı iki sütunlu
 * düzende: solda ürünün ne yaptığı, sağda tek işin formu.
 *
 * ### UI Tur 2 §7.15 — `SplitAuthLayout` kit bileşenine kablolandı
 *
 * Görsel kabuk (iki sütun, hero metni, radyal gradyan, form kartı ölçüleri) artık
 * `packages/ui`'deki `SplitAuthLayout`'ta yaşıyor (train 3). Bu dosya yalnız DOMAIN
 * bilgisini (hero metni, üç özellik satırı, ikonlar) besliyor — kit bunları BİLMİYOR,
 * `packages/ui/CLAUDE.md`'nin kuralı bu. Dışa açılan `AuthLayoutProps` DEĞİŞMEDİ: yedi
 * çağıran (`RegisterPage`, `LoginPage`, `ForgotPasswordPage`, `VerifyEmailPage`,
 * `ResetPasswordPage`, `AuthErrorPage`, …) tek satır değiştirmeden çalışmaya devam ediyor.
 *
 * ### Sol sütun DAR EKRANDA GİZLENİYOR
 *
 * ⚠ Bu, "mobilde işlevi kes" DEĞİL: sol sütun tanıtım metni, bir kontrol değil. Telefonda
 * formu ekranın yarısına sıkıştırmak, asıl işi zorlaştırmak olurdu. Kabuğun taşıdığı
 * hiçbir düğme ya da bağlantı orada yok — hepsi formun içinde. (`SplitAuthLayout`'un
 * kendi kuralı, aynen korunuyor.)
 *
 * ### Mockup'taki dil seçici, tema anahtarı, alt bağlantılar ve masa fotoğrafı YOK
 *
 * ATLANDI — ürün yüzeyi yok. Dil seçimi diye bir özellik bulunmuyor; tema tercihi hesap
 * ekranında (`/tercihler`) ve oturum açmadan uygulanamıyor; yasal metinlerin kanonik
 * girişi kayıt formundaki onay alanları (`LegalConsentFields`), ayrı bir alt menü değil;
 * `/gizlilik`, `/kullanim-sartlari`, `/destek` gibi rotalar router'da YOK. Çalışmayan bir
 * kontrol ya da kırık bir bağlantı koymak ADR-0014 ihlali olurdu — bu yüzden `headerEnd`
 * ve `footer` yuvaları hiç doldurulmuyor. Fotoğraf `SplitAuthLayout`'un kendi kararıyla
 * zaten radyal gradyana döndü (§7.15).
 */
export interface AuthLayoutProps {
  /** Kartın başlığı — ekranın `<h1>`'i. */
  title: string;
  /** Başlığın altındaki açıklama. */
  description?: string;
  /** Başlığın üstündeki ikon çipi. */
  icon?: ReactNode;
  children: ReactNode;
}

const OZELLIKLER = [
  {
    key: "sozlesme",
    icon: <ContractIcon />,
    title: "Merkezi sözleşme yönetimi",
    description: "Tüm sözleşmelerinizi tek bir yerde, düzenli ve erişilebilir tutun.",
  },
  {
    key: "hatirlatma",
    icon: <ClientAreaIcon />,
    title: "Akıllı hatırlatmalar",
    description: "Yaklaşan kritik tarihleri bildirimlerle takip edin.",
  },
  {
    key: "guvenlik",
    icon: <ShieldIcon />,
    title: "Güvenli çalışma alanı",
    description: "Erişim yetkileri çalışma alanı bazında ayrıştırılır.",
  },
] as const;

export function AuthLayout({ title, description, icon, children }: AuthLayoutProps) {
  return (
    <SplitAuthLayout
      heroTitle={
        <>
          {/*
            ⚠ `<br />` görsel satırı kırıyor ama metin akışında boşluk BIRAKMIYOR: erişim
            ağacında ve kopyalanan metinde "Sözleşme yönetiminidaha kolay hale getirin."
            çıkıyordu. Açık boşluk hem satır kırılmasını hem doğru okunmayı korur.
          */}
          Sözleşme yönetimini <br />
          daha kolay hale getirin.
        </>
      }
      heroDescription="Tüm sözleşmelerinizi tek bir yerde yönetin, kritik tarihleri kaçırmayın."
      features={OZELLIKLER}
      title={title}
      description={description}
      icon={icon}
      heroImageSrc="/images/auth-contract-workspace.png"
      heroImageAlt="Lacivert dosya üzerinde düzenlenmiş sözleşme belgeleri"
    >
      {children}
    </SplitAuthLayout>
  );
}
